import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { SESSION_IDLE_TIMEOUT_SECONDS } from 'constant/timing';

/** Absolute expiry timestamp (ms). Kept for cross-tab sync and backward compatibility. */
export const SESSION_IDLE_EXPIRES_AT_KEY = '_expiredTime';

/** Last user-interaction timestamp (ms). Written synchronously so it survives tab close. */
export const SESSION_LAST_ACTIVITY_AT_KEY = '_sessionLastActivity';

const idleTimeoutMs = () => SESSION_IDLE_TIMEOUT_SECONDS * 1000;

export const getSessionLastActivityTime = (): number | null => {
  const raw = localStorage.getItem(SESSION_LAST_ACTIVITY_AT_KEY);
  if (!raw) return null;

  const value = parseInt(raw, 10);
  return Number.isNaN(value) ? null : value;
};

export const getSessionIdleExpiresAt = (): number => {
  const raw = localStorage.getItem(SESSION_IDLE_EXPIRES_AT_KEY);
  if (!raw) return 0;

  const value = parseInt(raw, 10);
  return Number.isNaN(value) ? 0 : value;
};

export const isSessionIdleExpired = (now = Date.now()): boolean => {
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  if (!token) return false;

  const lastActivity = getSessionLastActivityTime();
  const timeoutMs = idleTimeoutMs();

  if (lastActivity !== null && now - lastActivity > timeoutMs) {
    return true;
  }

  const expiresAt = getSessionIdleExpiresAt();
  return expiresAt > 0 && expiresAt < now;
};

/**
 * Records user activity and pushes the idle deadline forward.
 * Persists synchronously so a tab/browser close cannot drop the update.
 */
export const touchSessionActivity = (now = Date.now()): void => {
  localStorage.setItem(SESSION_LAST_ACTIVITY_AT_KEY, String(now));
  localStorage.setItem(
    SESSION_IDLE_EXPIRES_AT_KEY,
    String(now + idleTimeoutMs()),
  );
};

/** Updates only last-activity (cheap, per-event). Expiry is rescheduled separately. */
export const touchSessionLastActivitySync = (now = Date.now()): void => {
  localStorage.setItem(SESSION_LAST_ACTIVITY_AT_KEY, String(now));
};

export const clearSessionIdle = (): void => {
  localStorage.removeItem(SESSION_IDLE_EXPIRES_AT_KEY);
  localStorage.removeItem(SESSION_LAST_ACTIVITY_AT_KEY);
};

export type SessionIdleBootstrapState = 'no-token' | 'expired' | 'ok';

/**
 * Runs synchronously before auth restores a session from localStorage.
 * Returns `expired` when the idle window has elapsed; `ok` seeds markers on fresh login.
 */
export const enforceSessionIdleOnBootstrap = (): SessionIdleBootstrapState => {
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  if (!token) return 'no-token';

  if (isSessionIdleExpired()) {
    return 'expired';
  }

  const lastActivity = getSessionLastActivityTime();
  const expiresAt = getSessionIdleExpiresAt();
  if (lastActivity === null && expiresAt <= 0) {
    touchSessionActivity();
  }

  return 'ok';
};

/**
 * Clears auth tokens and idle markers without calling logout APIs.
 * Used when the idle timeout fires before React has mounted.
 */
export const clearAuthSessionStorage = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};
